import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { WS_COLOR, WS_STATUS } from './tokens';

export type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

export interface SaveBarProps {
  state: SaveState;
  /** Says what happened, e.g. "Saved at 14:02" or why saving failed. */
  message?: string;
  /** Buttons: one filled primary (Save), the rest tonal. */
  children: ReactNode;
}

const DEFAULT_MESSAGE: Record<SaveState, string> = {
  clean: 'No changes',
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  saved: 'Saved',
  error: "Couldn't save. Your changes are still here.",
};

/**
 * The fixed save row at the foot of an editing layout. The state is always written out and
 * announced politely (errors at once); the bar never moves with the form's scroll.
 */
export function SaveBar({ state, message, children }: SaveBarProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
        mt: 3,
        px: 4,
        py: 3,
        backgroundColor: WS_COLOR.surface,
        border: `1px solid ${WS_COLOR.border}`,
        borderRadius: '12px',
      }}
    >
      <Typography
        variant="body2"
        role={state === 'error' ? 'alert' : 'status'}
        sx={{
          color: state === 'error' ? WS_STATUS.error.ink : WS_COLOR.textMuted,
          fontWeight: state === 'dirty' || state === 'error' ? 600 : 400,
        }}
      >
        {message ?? DEFAULT_MESSAGE[state]}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>{children}</Box>
    </Box>
  );
}
